// Considered: ApproachHeaderMolecule (molecules/marketplace) — rejected because it is typed to
// Approach + MarketplaceMetrics and renders marketplace affordances (star rating, deploy action)
// that a read-only type header must not offer. This is typed to Schema + LineageResult and its
// load-bearing element is the lineage breadcrumb, which has no analogue there. Composes the
// existing GlyphAtom and StatusBadgeAtom rather than restyling either. See spec Reuse Audit.
// Delegates its structural shell (back button, glyph tile, title row, actions, below-title slot)
// to EditorHeaderMolecule — see that molecule's own header comment.
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'localization';
import { getTranslation } from '@center-platform/spheres/identity';
import type { Schema } from 'types/schema';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import { StatusBadgeAtom } from '../../atoms/StatusBadge';
import type { StatusBadgeTone } from '../../atoms/StatusBadge';
import EditorHeaderMolecule from '../EditorHeader/EditorHeaderMolecule';
import type { LineageResult } from '../../../hooks/useSphereType';
import './SphereTypeHeaderMolecule.scss';

export interface SphereTypeHeaderMoleculeProps {
  schema: Schema;
  lineage: LineageResult | null;
  /** Header action slot (e.g. Publish / Save draft) — additive; existing callers render unchanged. */
  actions?: React.ReactNode;
  /**
   * `published v{{n}}` when a version exists, `draft` while unpublished changes are pending,
   * or `unknown` when the read resolved neither — `header.t-version` (`type-composer.jsx:820`)
   * renders unconditionally in the contract, so this pill never disappears; an invented version
   * number would be worse than saying the version is unknown.
   */
  versionPill?: { published: false } | { published: true; version: number } | { unknown: true };
  /**
   * Org name for the scope chip on a non-platform type, resolved by the caller (e.g. via
   * `usePublisherName(schema.tenant_id)`). Absent — including while that lookup is loading —
   * falls back to the generic `scope.organization` label; a platform schema always reads
   * `scope.platform` regardless of this prop.
   */
  scopePill?: { organizationName: string };
  /**
   * `type-composer.jsx:800` (`s-back`) — leaves the composer for the gallery it was opened
   * from. Absent renders no back button rather than a dead one — a caller that opens the
   * composer as a standalone route (no gallery to return to) has nothing to wire this to.
   */
  onBack?: () => void;
}

const SphereTypeHeaderMolecule: React.FC<SphereTypeHeaderMoleculeProps> = ({
  schema,
  lineage,
  actions,
  versionPill,
  scopePill,
  onBack,
}) => {
  const { t, i18n } = useTranslation();

  // `type-composer.jsx:810-821` shows only the immediate parent, never a full ancestor
  // chain — a multi-level breadcrumb nav here would be structure the contract doesn't have.
  const immediateParent = lineage?.ancestors[0] ?? null;

  // `StatusBadgeAtomProps` is a union of two variants, so a conditional written inline in the
  // JSX attribute widens its literals to `string` and stops matching `StatusBadgeTone`.
  const versionTone: StatusBadgeTone =
    versionPill && 'published' in versionPill && versionPill.published ? 'success' : 'neutral';

  return (
    <EditorHeaderMolecule
      blockClassName="sphere-type-header"
      data-testid="sphere-type-header"
      // `type-composer.jsx:800` (`s-back`). Only rendered when the caller supplies a return
      // route — the composer opened as a standalone deep link has nowhere to go back to.
      onBack={onBack}
      backAriaLabel={t('studio.typeEditor.header.back')}
      backTestId="sphere-type-header-back"
      backDatum="header.button"
      // `trail-composer.jsx:880` (`s-back`) renders with no corner rounding — `icon-square`'s
      // shape carries `--button-radius-icon` (TRT-1 br 0 vs 4); the tight 2px pad is scoped in
      // SphereTypeHeaderMolecule.scss (`&__back.button-atom`).
      backShape="rectangle"
      // `type-composer.jsx:801-807` (`t-glyph`) — the tinted tile the glyph itself sits inside.
      // The tile is GlyphAtom's own variant so every tile on the surface is the one treatment;
      // the contract's two datums (tile, then the mark inside it) are both emitted by the atom.
      glyph={
        <GlyphAtom
          variant="tile"
          glyph={schema.glyph}
          glyphColor={schema.glyph_color ?? undefined}
          fallbackText={schema.name}
          emptyIcon="lucide:database"
          className="sphere-type-header__glyph"
          aria-hidden
          data-tile-datum="header.tile"
          data-datum="header.glyph"
          data-kind="glyph"
        />
      }
      title={getTranslation(i18n, schema.localization_name_key, schema.name)}
      titleDatum="header.ident-name"
      actions={actions}
      // `type-rail/trail-composer.jsx:879-913` — the action cluster is the header's own flex
      // item, a sibling of the title block, not nested inside the name row: its height must not
      // stretch the title-row's cross-axis height (TRT-3 `header` region).
      actionsPlacement="header"
      below={
        // `type-composer.jsx:810-821` (`t-lineage`) — the immediate-parent link (or the
        // standalone "root" chip) plus this type's own name and its flags, in the
        // contract's order: name, abstract, sealed, hidden, scope, version.
        <div
          className="sphere-type-header__chips"
          data-datum="header.breadcrumb"
          data-kind="breadcrumb"
          data-container="header > group > breadcrumb"
        >
          {immediateParent ? (
            <>
              <Link
                className="sphere-type-header__crumb-link"
                to={`/studio/types/${immediateParent.schemaId}`}
                data-datum="header.t-parent"
                data-kind="link"
              >
                {getTranslation(i18n, immediateParent.nameKey, immediateParent.name)}
              </Link>
              <i
                className="pi pi-angle-right sphere-type-header__crumb-sep"
                aria-hidden="true"
              />
            </>
          ) : (
            <StatusBadgeAtom
              tone="neutral"
              label={t('studio.typeEditor.header.standaloneRoot')}
              size="small"
              data-datum="header.root"
              data-kind="chip"
            />
          )}
          <strong
            className="sphere-type-header__t-name"
            data-datum="header.t-name"
            data-kind="strong"
          >
            {getTranslation(i18n, schema.localization_name_key, schema.name)}
          </strong>
          {schema.is_abstract && (
            <StatusBadgeAtom
              tone="neutral"
              label={t('studio.typeEditor.header.state.abstract')}
              size="small"
              data-datum="header.abstract"
              data-kind="chip"
            />
          )}
          {schema.sealed && (
            <StatusBadgeAtom
              tone="warn"
              label={t('studio.typeEditor.header.state.sealed')}
              size="small"
              data-datum="header.sealed"
              data-kind="chip"
            />
          )}
          {schema.is_hidden && (
            <StatusBadgeAtom
              tone="neutral"
              label={t('studio.typeEditor.header.state.hidden')}
              size="small"
              data-datum="header.hidden"
              data-kind="chip"
            />
          )}
          <StatusBadgeAtom
            tone="neutral"
            label={
              schema.is_system_schema
                ? t('studio.typeEditor.header.scope.platform')
                : scopePill?.organizationName ?? t('studio.typeEditor.header.scope.organization')
            }
            size="small"
            className="sphere-type-header__scope"
            data-testid="sphere-type-header-scope-pill"
            data-datum="header.t-scope"
            data-kind="chip"
          />
          {versionPill && (
            <StatusBadgeAtom
              tone={versionTone}
              label={
                'unknown' in versionPill
                  ? t('studio.typeEditor.identity.versionUnknown')
                  : versionPill.published
                    ? t('studio.typeEditor.composer.header.versionPublished', { version: versionPill.version })
                    : t('studio.typeEditor.composer.header.versionDraft')
              }
              size="small"
              className="sphere-type-header__version"
              data-testid="sphere-type-header-version-pill"
              data-datum="header.t-version"
              data-kind="pill"
            />
          )}
        </div>
      }
    />
  );
};

SphereTypeHeaderMolecule.displayName = 'SphereTypeHeaderMolecule';

export default SphereTypeHeaderMolecule;
